import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { useReservationsApi } from "./useReservationsApi";
import { paymentsQueryKeys } from "./usePaymentsData";

export const reservationsQueryKeys = {
    reservations: (propertyId) => ['reservations', { propertyId }],
    reservation: (propertyId, reservationId) => ['reservation', { propertyId, reservationId }],
};

export const useReservationsData = (propertyId, reservationId, { fetch = true } = {}) => {
    const {
        findAllReservations,
        findReservationById,
        createReservation,
        updateReservation,
        deleteReservation,
        updateReservationPayments,
        abandonPayment,
        checkout,
    } = useReservationsApi();

    const queryClient = useQueryClient();

    const cachedReservations = queryClient.getQueryData(reservationsQueryKeys.reservations(propertyId));

    const setReservationInCache = (reservation) => {
        if (!reservation?.id || !propertyId) return;

        queryClient.setQueryData(
            reservationsQueryKeys.reservation(propertyId, reservation.id),
            reservation
        );

        const list = queryClient.getQueryData(reservationsQueryKeys.reservations(propertyId));
        if (!list) return;

        const exists = list.some((r) => String(r.id) === String(reservation.id));
        const updatedList = exists
            ? list.map((r) => (String(r.id) === String(reservation.id) ? reservation : r))
            : [...list, reservation];
        queryClient.setQueryData(reservationsQueryKeys.reservations(propertyId), updatedList);
    };

    const removeReservationFromCache = (id) => {
        queryClient.removeQueries(reservationsQueryKeys.reservation(propertyId, id));

        const list = queryClient.getQueryData(reservationsQueryKeys.reservations(propertyId));
        if (list) {
            queryClient.setQueryData(
                reservationsQueryKeys.reservations(propertyId),
                list.filter((r) => String(r.id) !== String(id))
            );
        }
    };

    const invalidateReservationPayments = (id) => {
        queryClient.invalidateQueries(paymentsQueryKeys.reservationPayments(propertyId, id));
    };

    const queryResults = useQuery({
        queryKey: reservationId
            ? reservationsQueryKeys.reservation(propertyId, reservationId)
            : reservationsQueryKeys.reservations(propertyId),
        queryFn: reservationId
            ? () => findReservationById(propertyId, reservationId)
            : () => findAllReservations(propertyId),
        initialData: reservationId
            ? () => cachedReservations?.find((r) => String(r.id) === String(reservationId))
            : undefined,
        staleTime: defaultStaleTime,
        enabled: fetch && !!propertyId && (reservationId ? !!reservationId : true),
    });

    const _createReservation = async (reservation) => {
        const createdReservation = await createReservation(propertyId, reservation);
        setReservationInCache(createdReservation);
        return createdReservation;
    };

    const _updateReservation = async (reservation) => {
        const { payments, ...reservationWithoutPayments } = reservation;
        const updatedReservation = await updateReservation(propertyId, reservationWithoutPayments);
        setReservationInCache(updatedReservation);
        return updatedReservation;
    };

    const _deleteReservation = async (id) => {
        await deleteReservation(propertyId, id);
        removeReservationFromCache(id);
    };

    const _updateReservationPayments = async (id, payments) => {
        const updatedPayments = await updateReservationPayments(propertyId, id, payments);
        queryClient.setQueryData(paymentsQueryKeys.reservationPayments(propertyId, id), updatedPayments);
        return updatedPayments;
    };

    const _abandonPayment = async (orderId, paymentIds) => {
        const order = await abandonPayment(propertyId, orderId, paymentIds);
        setReservationInCache(order);
        invalidateReservationPayments(orderId);
        return order;
    };

    const _checkout = async (orderId, paymentMethod, paymentIds) => {
        const result = await checkout(propertyId, orderId, paymentMethod, paymentIds);
        setReservationInCache(result.order);
        invalidateReservationPayments(orderId);
        return result;
    };

    return {
        queryResults,
        createReservation: _createReservation,
        updateReservation: _updateReservation,
        deleteReservation: _deleteReservation,
        updateReservationPayments: _updateReservationPayments,
        abandonPayment: _abandonPayment,
        checkout: _checkout,
    };
};
